import React from 'react';
import { Helmet } from 'react-helmet-async';

const udemyLink = 'https://www.udemy.com/course/machine-learning-deep-learning-model-deployment/?referralCode=E1443BEB8990D90B8A22';

const CourseAIEngineering: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>AI Engineering: Model Deployment, MLOps & Agentic AI | FutureXSkills</title>
        <meta name="description" content="Learn to build, deploy, and serve Machine Learning, Deep Learning, and AI models with Python, Flask, TensorFlow, PyTorch, MLflow, OpenAI SDK & AI Agents. Hands-on AI Engineering course with 100K+ students!" />
        <meta name="keywords" content="AI engineering, model deployment, MLOps, agentic AI, AI agents, OpenAI SDK, large language models, LLM, machine learning deployment, deep learning course, TensorFlow serving, PyTorch deployment, Flask API, MLflow, GitHub Copilot, FastAPI, prompt engineering" />
        <meta property="og:title" content="AI Engineering: Model Deployment, MLOps & Agentic AI" />
        <meta property="og:description" content="Build, deploy, and serve ML, Deep Learning, and AI models using Python, TensorFlow, PyTorch, MLflow, OpenAI SDK, and AI Agents. Hands-on projects, expert instruction." />
        <meta property="og:url" content="https://futurexskills.com/courses/ai-engineering" />
        <link rel="canonical" href="https://futurexskills.com/courses/ai-engineering" />
      </Helmet>
      <div className="max-w-6xl mx-auto py-16 px-4">
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold mb-6 gradient-text">
          AI Engineering
          <span className="block text-accent-400">Model Deployment, MLOps & Agentic AI</span>
        </h1>

        <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto leading-relaxed">
          🚀 Learn how to <span className="text-blue-600 font-semibold">build, deploy, and serve</span> Machine
          Learning, Deep Learning, and AI models — and go beyond development into LLMs, OpenAI SDK, AI Agents,
          MLOps, and AI-assisted development workflows.
        </p>

        <div className="mb-8">
          <a
            href={udemyLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold px-8 py-4 rounded-lg hover:from-yellow-400 hover:to-orange-400 transition-all transform hover:scale-105 shadow-lg"
          >
            <span className="mr-2">🎓</span>
            View Course on Udemy
            <span className="ml-2">→</span>
          </a>
        </div>
      </div>

      {/* What You'll Learn Section */}
      <div className="mb-12">
        <h2 className="text-3xl font-bold mb-8 text-center">
          🎯 <span className="gradient-text">What You'll Master</span>
        </h2>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="glass-card p-6 hover:bg-white/10 transition-all">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🤖</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-2">Model Building & Persistence</h3>
                <p className="text-gray-700">Build classification models with Scikit-learn, save and load models and scalers, and export them across local and Colab environments</p>
              </div>
            </div>
          </div>

          <div className="glass-card p-6 hover:bg-white/10 transition-all">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🌐</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-purple-600 mb-2">REST APIs & Cloud Deployment</h3>
                <p className="text-gray-700">Build REST APIs with Flask, deploy on cloud virtual machines, and ship serverless ML APIs with Cloud Functions, AWS Lambda & API Gateway</p>
              </div>
            </div>
          </div>

          <div className="glass-card p-6 hover:bg-white/10 transition-all">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🔥</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-red-600 mb-2">Deep Learning Deployment</h3>
                <p className="text-gray-700">Serve TensorFlow, Keras, and PyTorch models, convert PyTorch to TensorFlow with ONNX, and deploy with TensorFlow.js and JavaScript</p>
              </div>
            </div>
          </div>

          <div className="glass-card p-6 hover:bg-white/10 transition-all">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">📈</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-cyan-600 mb-2">MLOps & Experiment Tracking</h3>
                <p className="text-gray-700">Track training experiments and deployments with MLflow, and run it on Google Colab and Databricks</p>
              </div>
            </div>
          </div>

          <div className="glass-card p-6 hover:bg-white/10 transition-all">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🧠</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-green-600 mb-2">Generative AI & LLMs</h3>
                <p className="text-gray-700">Explore GPT models, the OpenAI API, prompt engineering, and build chatbots with text, speech, and image generation</p>
              </div>
            </div>
          </div>

          <div className="glass-card p-6 hover:bg-white/10 transition-all">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🧑‍💻</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-yellow-600 mb-2">AI-Assisted Development</h3>
                <p className="text-gray-700">Use GitHub Copilot Agent Mode and vibe coding to build models, REST APIs, and interactive ML web apps with a single prompt</p>
              </div>
            </div>
          </div>

          <div className="glass-card p-6 hover:bg-white/10 transition-all md:col-span-2">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-indigo-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">⚙️</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-indigo-600 mb-2">Building AI Agents with the OpenAI SDK</h3>
                <p className="text-gray-700">Understand what an AI Agent is, build your first agent and a tool-calling agent with the OpenAI SDK, and deploy an AI Agent as a REST API using FastAPI</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Technologies Section */}
      <div className="mb-12">
        <h2 className="text-3xl font-bold mb-8 text-center">
          🛠️ <span className="gradient-text">Technologies & Tools</span>
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { name: 'Python', icon: '🐍', color: 'blue' },
            { name: 'TensorFlow', icon: '🧠', color: 'yellow' },
            { name: 'PyTorch', icon: '🔥', color: 'red' },
            { name: 'Flask & FastAPI', icon: '⚡', color: 'green' },
            { name: 'Scikit-learn', icon: '📊', color: 'purple' },
            { name: 'MLflow', icon: '📈', color: 'cyan' },
            { name: 'OpenAI SDK', icon: '🤖', color: 'emerald' },
            { name: 'GitHub Copilot', icon: '🧑‍💻', color: 'yellow' },
            { name: 'AI Agents', icon: '⚙️', color: 'teal' },
            { name: 'Cloud Functions & AWS Lambda', icon: '☁️', color: 'blue' },
            { name: 'TensorFlow.js', icon: '💛', color: 'yellow' },
            { name: 'ONNX', icon: '🔄', color: 'purple' }
          ].map((tech) => (
            <div key={tech.name} className="glass-card p-4 text-center hover:bg-white/10 transition-all">
              <div className="text-3xl mb-2">{tech.icon}</div>
              <div className={`text-${tech.color}-400 font-semibold`}>{tech.name}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Course Modules */}
      <div className="mb-12">
        <h2 className="text-3xl font-bold mb-8 text-center">
          📚 <span className="gradient-text">Course Structure</span>
        </h2>

        <div className="space-y-4">
          {[
            {
              icon: '🔍',
              title: 'Model Building, Persistence & REST APIs',
              desc: 'Build a classification model with Scikit-learn, save and load models and scalers, export across local and Colab environments, and build a Flask REST API and test it locally',
              topics: ['Classification Models', 'Model & Scaler Persistence', 'Cross-Environment Export', 'Flask REST API']
            },
            {
              icon: '☁️',
              title: 'Cloud & Serverless Deployment',
              desc: 'Deploy a Machine Learning REST API on a cloud virtual machine, and create serverless ML REST APIs using Cloud Functions and AWS Lambda with API Gateway',
              topics: ['Cloud Virtual Machines', 'Cloud Functions', 'AWS Lambda & API Gateway', 'Serverless APIs']
            },
            {
              icon: '🧠',
              title: 'Deep Learning Model Deployment',
              desc: 'Build and deploy TensorFlow, Keras, and PyTorch models, convert PyTorch models to TensorFlow with ONNX, and build REST APIs for TensorFlow and PyTorch models',
              topics: ['TensorFlow Serving', 'PyTorch Deployment', 'ONNX Conversion', 'REST APIs for DL Models']
            },
            {
              icon: '💬',
              title: 'NLP, Frontend & MLOps',
              desc: 'Deploy TF-IDF and text classification models for Twitter sentiment analysis, deploy models with TensorFlow.js and JavaScript, and track experiments and deployments with MLflow on Colab and Databricks',
              topics: ['Sentiment Analysis', 'TensorFlow.js', 'MLflow Tracking', 'Colab & Databricks']
            },
            {
              icon: '✨',
              title: 'Generative AI & LLM Fundamentals',
              desc: 'Learn Generative AI and LLM fundamentals, the evolution of GPT models, and invoke OpenAI text-to-speech, chat, text generation, and image generation models — then build a chatbot with the OpenAI API and ChatGPT',
              topics: ['Generative AI & LLMs', 'OpenAI Models', 'Prompt Engineering', 'Chatbot with OpenAI API']
            },
            {
              icon: '🧑‍💻',
              title: 'AI-Assisted Development with GitHub Copilot',
              desc: 'Use GitHub Copilot and Agent Mode for model development, vibe code ML models with a single prompt, and build REST APIs and interactive ML web apps with Copilot Agent Mode',
              topics: ['GitHub Copilot Agent Mode', 'Vibe Coding', 'Copilot-Built REST APIs', 'Interactive ML Web Apps']
            },
            {
              icon: '⚙️',
              title: 'Building AI Agents with the OpenAI SDK',
              desc: 'Understand what an AI Agent is, build your first AI Agent and a tool-calling AI Agent using the OpenAI SDK, and deploy an AI Agent as a REST API using FastAPI',
              topics: ['What is an AI Agent?', 'OpenAI SDK Agents', 'Tool Calling Agents', 'FastAPI Agent Deployment']
            }
          ].map((module, index) => (
            <div key={index} className="glass-card p-6 hover:bg-white/10 transition-all">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-2xl flex-shrink-0">
                  {module.icon}
                </div>
                <div className="flex-1">
                  <div className="mb-2">
                    <h3 className="text-xl font-semibold text-blue-600">{module.title}</h3>
                  </div>
                  <p className="text-gray-700 mb-3">{module.desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {module.topics.map((topic, topicIndex) => (
                      <span key={topicIndex} className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-full">
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Prerequisites & Bonus */}
      <div className="grid md:grid-cols-2 gap-8 mb-12">
        <div className="glass-card p-6">
          <h3 className="text-2xl font-bold mb-4 flex items-center">
            <span className="mr-3">📋</span>
            <span className="gradient-text">Prerequisites</span>
          </h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-center"><span className="text-green-600 mr-2">✓</span>No prior Machine Learning or Deep Learning experience required</li>
            <li className="flex items-center"><span className="text-green-600 mr-2">✓</span>A basic understanding of Python programming is recommended</li>
            <li className="flex items-center"><span className="text-green-600 mr-2">✓</span>Google Cloud Platform (GCP) free trial account recommended for cloud labs</li>
            <li className="flex items-center"><span className="text-green-600 mr-2">✓</span>Enthusiasm to learn!</li>
          </ul>
        </div>

        <div className="glass-card p-6">
          <h3 className="text-2xl font-bold mb-4 flex items-center">
            <span className="mr-3">🎁</span>
            <span className="gradient-text">What You'll Walk Away With</span>
          </h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-center"><span className="text-purple-600 mr-2">🤖</span>Build, deploy & serve ML, DL and AI-powered applications</li>
            <li className="flex items-center"><span className="text-purple-600 mr-2">📈</span>Hands-on with Python, FastAPI, TensorFlow, PyTorch & MLflow</li>
            <li className="flex items-center"><span className="text-purple-600 mr-2">🎯</span>Practical experience with the OpenAI SDK and AI Agents</li>
            <li className="flex items-center"><span className="text-purple-600 mr-2">💡</span>Modern AI Engineering & AI-assisted development workflows</li>
          </ul>
        </div>
      </div>

      {/* Call to Action */}
      <div className="text-center bg-gradient-to-r from-blue-500/20 to-purple-600/20 rounded-2xl p-8">
        <h3 className="text-2xl font-bold mb-4">🚀 Ready to Become an AI Engineer?</h3>
        <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
          Join thousands of students learning to build, deploy, and serve ML, Deep Learning, and AI models —
          and master modern AI Engineering with LLMs, OpenAI SDK, AI Agents, and MLOps along the way!
        </p>
        <a
          href={udemyLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold px-8 py-4 rounded-lg hover:from-blue-400 hover:to-purple-500 transition-all transform hover:scale-105 shadow-lg"
        >
          <span className="mr-2">🎓</span>
          Start Learning Today
          <span className="ml-2">→</span>
        </a>
      </div>
    </div>
    </>
  );
};

export default CourseAIEngineering;
